import { PanelCard, EmptyState, Chip, Badge, PagedFooter, TextLink } from '../../components/ui';
import Spinner from '../../components/ui/Spinner';
import ErrorPanel from '../../components/ui/ErrorPanel';
import { FaClipboardList } from '../../components/ui/icons';
import { Link } from 'react-router-dom';
import { useCycles } from '../../api/hooks';
import { usePagination } from '../../lib/usePagination';
import { LabelledSteps, fmtDelta } from '../class_performance/components/cycleBits';
import { dayMonth, shortSection } from '../class_performance/lib/cpBits';

interface Props { gradeId: number; sectionId: number; gradeName?: string; sectionName?: string }

export default function SectionCycles({ gradeId, sectionId, gradeName, sectionName }: Props) {
  const q = useCycles({ grade: gradeId, section: sectionId, subject: 'all' });
  const pg = usePagination(q.data?.cycles ?? [], 10);
  const short = shortSection(gradeName, sectionName) || 'this section';
  const allCycles = `/school/section/${sectionId}?view=analytics#cycles`;
  let body;
  if (q.isLoading) body = <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  else if (q.isError || !q.data) body = <ErrorPanel />;
  else if (q.data.cycles.length === 0) {
    body = <EmptyState compact icon={<FaClipboardList />} title="No remediation cycles yet" body="Pick weak chapters to start one." />;
  } else {
    body = (
      <>
      <ul>
        {pg.pageItems.map((c) => {
          const s = c.gain.summary;
          const d = s?.mean_delta;
          const meta = [
            c.subject?.name,
            `${c.target_count} ${c.target_count === 1 ? 'student' : 'students'}`,
            c.status === 'assigned' && c.assigned_at ? `remedial exam assigned ${dayMonth(c.assigned_at)}`
              : s?.crossed_pass_share != null ? `${Math.round(s.crossed_pass_share * 100)}% crossed the pass mark` : null,
          ].filter(Boolean).join(' · ');
          return (
            <li key={c.id} className="mb-2.5 rounded-[14px] bg-panel p-3.5">
              <div className="flex items-start justify-between gap-2.5">
                <div className="min-w-0">
                  <p className="font-semibold">#{c.id} · {c.target_chapters.map((t) => t.name).join(', ')}</p>
                  <p className="text-xs text-muted">{meta}</p>
                </div>
                {d != null
                  ? <Chip tone={d > 0 ? 'up' : d < 0 ? 'down' : 'neutral'}>{fmtDelta(d)} pts</Chip>
                  : c.status === 'assigned' ? <Badge tone="info">Awaiting retest</Badge> : null}
              </div>
              <div className="mt-3.5 overflow-x-auto"><LabelledSteps status={c.status} /></div>
            </li>
          );
        })}
      </ul>
      <PagedFooter className="mt-3.5" label="Cycles pagination" {...pg} onPage={pg.setPage} />
      </>
    );
  }
  return (
    <PanelCard title={`Remediation in ${short}`} actions={<TextLink to={allCycles}>All cycles →</TextLink>}>
      {body}
      <Link className="mt-1 inline-flex min-h-[44px] w-full items-center justify-center rounded-[12px] border border-navy bg-navy px-4 text-sm font-semibold text-white no-underline hover:bg-navy-2 hover:text-white" to={`/school/section/${sectionId}?view=analytics#chapters`}>Pick weak chapters for {short}</Link>
    </PanelCard>
  );
}
